import { Injectable } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Coupon } from './coupon.model';
import { AuditService } from '../services/audit.service';

@Injectable({ providedIn: 'root' })
export class CouponService {

  private readonly maxStoredValueAmount = 500;

  constructor(
    private afs: AngularFirestore,
    private afAuth: AngularFireAuth,
    private audit: AuditService
  ) {}

  getCoupons$(): Observable<Coupon[]> {
    return this.afs.collection<Coupon>('coupons', ref => ref.orderBy('validUntil', 'desc')).snapshotChanges().pipe(
      map(docs => docs.map(doc => ({
        ...doc.payload.doc.data() as Coupon,
        code: doc.payload.doc.id
      })))
    );
  }

  getCouponsByCompany$(companyId: string): Observable<Coupon[]> {
    return this.getCoupons$().pipe(
      map(coupons => coupons.filter(c => c.companyId === companyId || c.companyId === null))
    );
  }

  async createCoupon(coupon: Partial<Coupon>): Promise<void> {
    this.validateCouponLimits(coupon);

    const code = coupon.code.toUpperCase();
    const currentUser = await this.afAuth.currentUser;

    return this.afs.collection('coupons').doc(code).set({
      ...coupon,
      code,
      usedCount: 0,
      usedBy: [],
      createdBy: currentUser?.email || 'unknown',
      createdByUid: currentUser?.uid || null,
      createdAt: new Date()
    });
  }

  updateCoupon(code: string, data: Partial<Coupon>): Promise<void> {
    this.validateCouponLimits(data);

    return this.afs.collection('coupons').doc(code).update({
      ...data,
      companyId: firebase.firestore.FieldValue.delete(),
      siteId: firebase.firestore.FieldValue.delete(),
      maxUses: firebase.firestore.FieldValue.delete(),
      singleUsePerUser: firebase.firestore.FieldValue.delete()
    });
  }

  toggleActive(code: string, isActive: boolean): Promise<void> {
    return this.afs.collection('coupons').doc(code).update({ isActive });
  }

  async deleteCoupon(code: string): Promise<void> {
    const reason = await this.audit.promptDeleteReason('coupon', code);
    if (reason === null) return; // cancelled — leave the coupon untouched
    const ref = this.afs.collection('coupons').doc(code);
    // Stamp who + why so the audit trigger can attribute the delete, then remove.
    await ref.update(this.audit.stamp('delete', reason));
    await ref.delete();
  }

  private validateCouponLimits(coupon: Partial<Coupon>) {
    if (coupon.discountType && coupon.discountType !== 'fixed') {
      throw new Error('Only gavekort is supported');
    }

    if (coupon.discountType === 'fixed' && Number(coupon.discountValue) > this.maxStoredValueAmount) {
      throw new Error(`Gavekort cannot be more than NOK ${this.maxStoredValueAmount}`);
    }
  }
}
